import React, { useState, useEffect } from 'react';
import { SpaceBetween } from '@amzn/awsui-components-react';
import ModuleListModel from '../../../data/site/module_list_module';
import {
    Alert,
    ExpandableSection,
    Header,
    StatusIndicator,
    Table,
    TableProps,
    Pagination,
    ButtonDropdown,
    TabsProps,
    Button
} from '@amzn/geist-ui-components';
import ExportToCSVButton from '../../../components/export_to_csv_button';
import Input from '@amzn/meridian/input';
import RadioButton from "@amzn/meridian/radio-button";
import UpdateWorkflowRole from "../../mdlUpdateWorkflowTask/mdl_update_workflow_task_view";
import Icon from '@amzn/meridian/icon';
import filterTokens from "@amzn/meridian-tokens/base/icon/filter";
import gridTokens from "@amzn/meridian-tokens/base/icon/grid";
import CreateWorkflowModal from './CreateWorkflowModal'; // Import the new CreateWorkflowModal component

const dummyItems: Item[] = [
    { name: 'Modify MDL', assignee: 'John', business_milestone: 'Composite 1.0', due_date: '2024-07-23', comments: 'None' },
    { name: 'Review MDL', assignee: 'Jane', business_milestone: 'Composite 1.0', due_date: '2024-08-01', comments: 'None' },
    { name: 'Release MDL', assignee: 'Joe', business_milestone: 'Composite 1.0', due_date: '2024-08-03', comments: 'None' },
];

const dummyProcesses: Process[] = [
    {
        id: '1',
        name: 'Process 1',
        attributes: new ModuleListModel({ number: '1', name: 'DBK1_Module_List', displayVersion: '0.1', stateString: 'In Progress', mdgTemplate: 'Template1', erpStatus: 'In Progress', moduleCountsTemplate: 'TemplateA' }),
        tasks: dummyItems,
        version: '0.1',
        status: 'In Progress',
        initiatedBy: 'Alice',
        dateStarted: '2024-07-01',
        dateCompleted: 'N/A',
    },
    {
        id: '2',
        name: 'Process 2',
        attributes: new ModuleListModel({ number: '2', name: 'DBK2_Module_List', displayVersion: '0.2', stateString: 'Completed', mdgTemplate: 'Template2', erpStatus: 'Completed', moduleCountsTemplate: 'TemplateB' }),
        tasks: dummyItems.slice(0, 2),
        version: '0.2',
        status: 'Completed',
        initiatedBy: 'Bob',
        dateStarted: '2024-06-01',
        dateCompleted: '2024-07-15',
    },
    {
        id: '3',
        name: 'Process 3',
        attributes: new ModuleListModel({ number: '3', name: 'DBK3_Module_List', displayVersion: '0.3', stateString: 'Completed', mdgTemplate: 'Template3', erpStatus: 'Completed', moduleCountsTemplate: 'TemplateC' }),
        tasks: dummyItems.slice(0, 1),
        version: '0.3',
        status: 'Completed',
        initiatedBy: 'Charlie',
        dateStarted: '2024-05-15',
        dateCompleted: '2024-06-20',
    },
];

interface Item {
    name: string;
    assignee: string;
    business_milestone: string;
    due_date: string;
    comments: string;
}

interface Process {
    id: string;
    name: string;
    attributes: ModuleListModel;
    tasks: Item[];
    version: string;
    status: string;
    initiatedBy: string;
    dateStarted: string;
    dateCompleted: string;
}

interface ModuleListTaskViewProps {
    moduleList: ModuleListModel;
}

export const ModuleListTaskView: React.FC<ModuleListTaskViewProps> = ({ moduleList }) => {
    const [selectedProcessId, setSelectedProcessId] = useState<string>(dummyProcesses[0].id);
    const selectedProcess = dummyProcesses.find(process => process.id === selectedProcessId) || dummyProcesses[0];
    const [filterText, setFilterText] = useState<string>('');
    const [processFilterText, setProcessFilterText] = useState<string>('');
    const [sortColumn, setSortColumn] = useState<keyof Item>('name');
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [paginatedItems, setPaginatedItems] = useState<readonly Item[]>([]);
    const [processPage, setProcessPage] = useState<number>(1);
    const [processPageSize, setProcessPageSize] = useState<number>(10);
    const [filteredProcesses, setFilteredProcesses] = useState<Process[]>(dummyProcesses);

    const [isModalOpen, setIsModalOpen] = useState(false);

    const workflowTabs: TabsProps.Tab[] = [
        {
            id: 'workflowTab',
            label: 'WorkFlow',
            content: (
                <div>
                    {/* Add your workflow selection logic here */}
                    <p>Content for the WorkFlow tab.</p>
                </div>
            ),
        },
        {
            id: 'tabTab',
            label: 'Tab',
            content: (
                <div>
                    <p>Content for the Tab tab.</p>
                </div>
            ),
        },
    ];

    useEffect(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const paginated = selectedProcess.tasks.slice(startIndex, startIndex + pageSize);
        setPaginatedItems(paginated);
    }, [currentPage, pageSize, selectedProcess]);

    useEffect(() => {
        const lowerCaseFilter = processFilterText.toLowerCase();
        setFilteredProcesses(
            dummyProcesses.filter(process =>
                process.name.toLowerCase().includes(lowerCaseFilter)
            )
        );
    }, [processFilterText]);

    const handleFilterChange = (event: React.SetStateAction<string>) => {
        setFilterText(event);
    };

    const handleProcessFilterChange = (event: React.SetStateAction<string>) => {
        setProcessFilterText(event);
    };

    const onSortingChange: TableProps['onSortingChange'] = (event) => {
        const sortingColumn = event.detail.sortingColumn.sortingField as keyof Item;
        if (sortColumn === sortingColumn) {
            setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
        } else {
            setSortColumn(sortingColumn);
            setSortDirection('ascending');
        }
    };

    const onProcessSortingChange: TableProps['onSortingChange'] = (event) => {
        const sortingColumn = event.detail.sortingColumn.sortingField as keyof Process;
        const direction = sortDirection === 'ascending' ? 'descending' : 'ascending';
        const sorted = [...filteredProcesses].sort((a, b) => {
            if (a[sortingColumn] < b[sortingColumn]) {
                return direction === 'ascending' ? -1 : 1;
            }
            if (a[sortingColumn] > b[sortingColumn]) {
                return direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });
        setFilteredProcesses(sorted);
        setSortDirection(direction);
    };

    const onPaginationChange = (event: any) => {
        setCurrentPage(event.detail.currentPageIndex);
    };

    const onProcessPaginationChange = (event: any) => {
        setProcessPage(event.detail.currentPageIndex);
    };

    const getTaskType = (itemName: string) => {
        const lowercaseName = itemName.toLowerCase();
        if (lowercaseName.includes('modify')) {
            return 'mdlModifyTask';
        } else if (lowercaseName.includes('release')) {
            return 'mdlReleaseTask';
        } else {
            return 'mdlReviewTask';
        }
    };

    const columnDefinitions: TableProps.ColumnDefinition<Item>[] = [
        {
            id: 'name',
            header: 'Name',
            cell: (item: Item) => (
                <a href={`${window.location.href}/${item.name}/${getTaskType(item.name)}`} rel="noopener noreferrer">
                    {item.name}
                </a>
            ),
            sortingField: 'name',
        },
        { id: 'assignee', header: 'Assignee', cell: (item: Item) => item.assignee, sortingField: 'assignee' },
        {
            id: 'business_milestone',
            header: 'Business Milestone',
            cell: (item: Item) => item.business_milestone,
            sortingField: 'business_milestone',
        },
        { id: 'due_date', header: 'Due Date', cell: (item: Item) => item.due_date, sortingField: 'due_date' },
        { id: 'comments', header: 'Comments', cell: (item: Item) => item.comments, sortingField: 'comments' },
    ];

    const processColumnDefinitions: TableProps.ColumnDefinition<Process>[] = [
        {
            id: 'radio',
            header: '',
            cell: (process: Process) => (
                <RadioButton
                    checked={selectedProcessId === process.id}
                    onChange={() => setSelectedProcessId(process.id)}
                    name="process"
                    value={process.id}
                />
            ),
            width: 50,
        },
        { id: 'name', header: 'Name', cell: (process: Process) => process.name, width: 150 },
        { id: 'version', header: 'Version', cell: (process: Process) => process.version, width: 100 },
        { id: 'status', header: 'Status', cell: (process: Process) => process.status, width: 100 },
        { id: 'initiatedBy', header: 'Initiated By', cell: (process: Process) => process.initiatedBy, width: 150 },
        { id: 'dateStarted', header: 'Date Started', cell: (process: Process) => process.dateStarted, width: 150 },
        { id: 'dateCompleted', header: 'Date Completed', cell: (process: Process) => process.dateCompleted, width: 150 },
        {
            id: 'diagram',
            header: 'Diagram',
            cell: () => (
                <a href="#">
                    <Icon tokens={gridTokens} color="primary" />
                </a>
            ),
            width: 100
        },
    ];

    const handleItemClick = (event: NonCancelableCustomEvent<ButtonDropdown.ItemClickDetails>) => {
        if (event.detail.id === 'createWorkflow') {
            setIsModalOpen(true);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    return (
        <SpaceBetween size="m">
            <Header
                variant="h1"
                actions={
                    <ButtonDropdown
                        items={[
                            { id: 'createWorkflow', text: 'Create a Workflow' },
                            { id: 'option1', text: 'Option 1' },
                            { id: 'option2', text: 'Option 2' },
                        ]}
                        onItemClick={handleItemClick}
                    >
                        <Button>
                            Action <Icon tokens={filterTokens} color="primary" />
                        </Button>
                    </ButtonDropdown>
                }
            >
                {selectedProcess.name}
            </Header>
            <CreateWorkflowModal
                open={isModalOpen}
                onClose={closeModal}
                workflowTabs={workflowTabs}
            />
            <Table
                columnDefinitions={processColumnDefinitions}
                items={filteredProcesses.slice((processPage - 1) * processPageSize, processPage * processPageSize)}
                loadingText={'Loading...'}
                filter={
                    <Input
                        placeholder="Filter processes..."
                        value={processFilterText}
                        onChange={handleProcessFilterChange}
                    />
                }
                sortingColumn={{ sortingField: 'name' }}
                sortingDescending={sortDirection === 'descending'}
                onSortingChange={onProcessSortingChange}
                pagination={
                    <Pagination
                        currentPageIndex={processPage}
                        pagesCount={Math.ceil(filteredProcesses.length / processPageSize)}
                        onChange={onProcessPaginationChange}
                    />
                }
            />
            <Header variant={'h3'}>{selectedProcess.name}</Header>
            <ExpandableSection
                variant="container"
                header={<Header variant={'h2'}>{'Update Workflow Role'}</Header>}
                defaultExpanded={true}
            >
                <UpdateWorkflowRole />
            </ExpandableSection>
            <ExpandableSection
                variant="container"
                header={<Header variant={'h2'}>{'Tasks'}</Header>}
                defaultExpanded={true}
            >
                <Table
                    columnDefinitions={columnDefinitions}
                    items={paginatedItems}
                    loadingText={'Loading...'}
                    filter={
                        <Input
                            placeholder="Filter items..."
                            value={filterText}
                            onChange={handleFilterChange}
                        />
                    }
                    sortingColumn={{ sortingField: sortColumn }}
                    sortingDescending={sortDirection === 'descending'}
                    onSortingChange={onSortingChange}
                    pagination={
                        <SpaceBetween size="s" direction="horizontal">
                            <Pagination
                                currentPageIndex={currentPage}
                                pagesCount={Math.ceil(selectedProcess.tasks.length / pageSize)}
                                onChange={onPaginationChange}
                            />
                            <ExportToCSVButton keys={Object.keys(dummyItems[0])} data={paginatedItems} filename="tasks_export.csv" />
                        </SpaceBetween>
                    }
                />
            </ExpandableSection>
        </SpaceBetween>
    );
};
